/**
 * lib/firebase.ts
 * ------------------------------------------------------------------
 * Firebase initialisation plus undefined-safe Firestore write
 * wrappers.
 *
 * Foundation round: every write to Firestore (setDoc, addDoc,
 * updateDoc) now goes through a tiny wrapper that runs the payload
 * through `stripUndefined` before delegating to the real Firestore
 * SDK. This kills a whole class of bug — Firestore rejects `undefined`
 * field values anywhere in a payload, and TypeScript can't catch
 * the cases where an optional field happens to resolve to undefined
 * at runtime.
 *
 * The wrappers have identical signatures to the SDK originals, so
 * callers only need to change their import path:
 *
 *     // Old:
 *     import { setDoc } from 'firebase/firestore';
 *     // New:
 *     import { setDoc } from '@/lib/firebase';
 *
 * No call-site changes. Sentinel field values
 * (serverTimestamp, arrayUnion, arrayRemove, deleteField, increment)
 * pass through untouched — `stripUndefined` detects them by their
 * internal `_methodName` discriminator.
 *
 * Note: writeBatch operations are NOT auto-wrapped. The two files
 * that use writeBatch (lib/teacherAssignments.ts, features/curriculum/
 * CurriculumManager.tsx) must call `stripUndefined` explicitly on
 * each batch payload. A future round may extend this wrapper to
 * cover batch writes.
 * ------------------------------------------------------------------
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import {
  getFirestore,
  type Firestore,
  setDoc as fsSetDoc,
  addDoc as fsAddDoc,
  updateDoc as fsUpdateDoc,
  type DocumentReference,
  type CollectionReference,
  type DocumentData,
  type SetOptions,
  type WithFieldValue,
  type PartialWithFieldValue,
  type UpdateData,
} from 'firebase/firestore';
import { getAuth, type Auth } from 'firebase/auth';
import { getFunctions, type Functions } from 'firebase/functions';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { stripUndefined } from '@/lib/sanitize';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env
    .VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

const app: FirebaseApp = initializeApp(firebaseConfig);

/* APP CHECK (SECURITY-BACKLOG item 3, 30 Oct 2026). Every request from this
 * bundle — the public application form included — carries a reCAPTCHA v3
 * attestation, so that once enforcement is switched on in the console a
 * script calling the API directly is refused. Until then it is monitor-only
 * and a failed attestation blocks nothing.
 *
 * The site key is public by design (it ships in every page) and is the one
 * the "lkcportal" web app is registered with; intake-teacher.html and the
 * parent pages use the same. Production builds only: reCAPTCHA refuses
 * localhost, so on the dev server it would only fill the console. */
const RECAPTCHA_V3_SITE_KEY =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined) || '6LftRyYtAAAAAIwq5yCFCaFr-z9xC128U2shxnrO';
if (import.meta.env.PROD && typeof window !== 'undefined') {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(RECAPTCHA_V3_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.warn('App Check not active:', e);
  }
}

export const db: Firestore = getFirestore(app);
export const auth: Auth = getAuth(app);
// The callables run in us-central1 (DEFAULT_REGION in functions/lib/
// config.js). NOT africa-south1, where the database and the Firestore
// triggers are: a function cannot change region in place, the CLI will
// not run both at once, and this region is baked into the bundle a
// browser has already loaded — so moving the callables costs an outage
// for everyone with the app open, which the latency does not justify.
//
// The region must be named here either way. Without it the SDK falls back
// to the legacy v1 URL us-central1-<project>.cloudfunctions.net/<fn>,
// which does not exist for a v2 function, and every callable fails as a
// CORS preflight error — the Round 13 bug, found when ParentSettings ->
// "Send invite" hit createParentAccount.
export const functions: Functions = getFunctions(app, 'us-central1');

export const storage: FirebaseStorage = getStorage(app);

export default app;

/* ============================================================ */
/* Undefined-safe write wrappers                                  */
/* ============================================================ */

/**
 * Internal: clean a Firestore write payload. Strips any `undefined`
 * values recursively. Throws if the payload is itself nullish (which
 * Firestore would reject with a confusing error anyway).
 */
function cleanPayload<T>(data: T, fnName: string): T {
  if (data === null || data === undefined) {
    throw new Error(
      `${fnName} received a null/undefined payload. ` +
        `Refusing to write — this almost certainly indicates a bug ` +
        `in the calling code.`,
    );
  }
  if (typeof data !== 'object') {
    return data;
  }
  return stripUndefined(data as unknown as Record<string, unknown>) as unknown as T;
}

/**
 * Undefined-safe wrapper around Firestore's `setDoc`. Strips
 * `undefined` values from the payload before writing. Otherwise
 * identical to the SDK function.
 */
export function setDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: WithFieldValue<AppModelType>,
): Promise<void>;
export function setDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: PartialWithFieldValue<AppModelType>,
  options: SetOptions,
): Promise<void>;
export function setDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: WithFieldValue<AppModelType> | PartialWithFieldValue<AppModelType>,
  options?: SetOptions,
): Promise<void> {
  const clean = cleanPayload(data, 'setDoc');
  // The overload accepts either signature depending on options presence.
  // We delegate to whichever overload of the SDK applies.
  if (options !== undefined) {
    return fsSetDoc(
      reference,
      clean as PartialWithFieldValue<AppModelType>,
      options,
    );
  }
  return fsSetDoc(reference, clean as WithFieldValue<AppModelType>);
}

/**
 * Undefined-safe wrapper around Firestore's `addDoc`.
 */
export function addDoc<AppModelType, DbModelType extends DocumentData>(
  reference: CollectionReference<AppModelType, DbModelType>,
  data: WithFieldValue<AppModelType>,
): Promise<DocumentReference<AppModelType, DbModelType>> {
  const clean = cleanPayload(data, 'addDoc');
  return fsAddDoc(reference, clean as WithFieldValue<AppModelType>);
}

/**
 * Undefined-safe wrapper around Firestore's `updateDoc`. Only the
 * object-form overload is wrapped (the codebase doesn't use the
 * variadic field/value pairs form). If you need that form, import
 * `updateDoc` directly from `firebase/firestore`.
 */
export function updateDoc<AppModelType, DbModelType extends DocumentData>(
  reference: DocumentReference<AppModelType, DbModelType>,
  data: UpdateData<DbModelType>,
): Promise<void> {
  const clean = cleanPayload(data, 'updateDoc');
  return fsUpdateDoc(reference, clean as UpdateData<DbModelType>);
}
